import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { STATUS } from "@/constants";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

export type NuggetGenerationQueued = {
  jobId: string;
  domainId: number;
};

export type HomeState = {
  nuggetGeneration: {
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
    lastQueued: NuggetGenerationQueued | null;
  };
};

export const homeInitialState: HomeState = {
  nuggetGeneration: {
    status: STATUS.IDLE,
    error: null,
    lastQueued: null,
  },
};

function parseNuggetGenerationResponse(
  data: unknown,
): NuggetGenerationQueued | null {
  if (typeof data !== "object" || data === null) {
    return null;
  }
  const row = data as Record<string, unknown>;
  if (typeof row.job_id !== "string" || typeof row.domain_id !== "number") {
    return null;
  }
  return { jobId: row.job_id, domainId: row.domain_id };
}

export const enqueueNuggetGeneration = createAsyncThunk<
  NuggetGenerationQueued,
  number,
  { rejectValue: string }
>("home/enqueueNuggetGeneration", async (domainId, { rejectWithValue }) => {
  const res = await apiFetch("/v1/nuggets/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ domain_id: domainId }),
  });
  if (!res.ok) {
    return rejectWithValue(await readApiErrorMessage(res));
  }
  const data: unknown = await res.json();
  const parsed = parseNuggetGenerationResponse(data);
  if (!parsed) {
    return rejectWithValue("Unexpected response shape");
  }
  return parsed;
});

const homeSlice = createSlice({
  name: "home",
  initialState: homeInitialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(enqueueNuggetGeneration.pending, (state) => {
        state.nuggetGeneration.status = STATUS.LOADING;
        state.nuggetGeneration.error = null;
      })
      .addCase(enqueueNuggetGeneration.fulfilled, (state, action) => {
        state.nuggetGeneration.status = STATUS.SUCCEEDED;
        state.nuggetGeneration.lastQueued = action.payload;
      })
      .addCase(enqueueNuggetGeneration.rejected, (state, action) => {
        state.nuggetGeneration.status = STATUS.FAILED;
        state.nuggetGeneration.lastQueued = null;
        state.nuggetGeneration.error =
          typeof action.payload === "string"
            ? action.payload
            : (action.error.message ?? "Request failed");
      });
  },
});

export const homeReducer = homeSlice.reducer;
